import { useState, useEffect, useRef } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { fetchPendingMaterials, submitMaterialDecisions } from '../../api/qcApi';
import { friendlyMessage } from '../../../api/apiClient';
import { formatMaterialPrice } from '../../../shared/formatPrice';
import { LoadingState, ErrorState, InlineAlert } from '../../../shared/components/status';

// ─── BoundingBoxPhoto ───────────────────────────────────────────────────────
// Draws the scene photo with a real rectangle outline over the region
// being reviewed.
//
// `alignSelf: 'flex-start'` is the important line here. Without it, this
// box is a flex child that stretches to match the height of its sibling
// (the candidate grid) -- which can be much taller than the actual photo
// once there are several candidates. Since the red box's position/size
// are percentages OF THIS BOX, a stretched box means those percentages
// land on the wrong scale (the stretched height, not the photo's real
// height), which is exactly what was causing the box to look
// misaligned. With alignSelf fixed, this box's height is always exactly
// the image's own rendered height -- the same frame the original
// labeller was looking at.
//
// It also reports its own rendered height (in pixels) up to the parent
// via onHeightChange, so the candidate grid alongside it can be capped
// to that same height and scroll internally instead of growing the
// whole card taller than the photo.
function BoundingBoxPhoto({ imagesrc, box, onHeightChange }) {
  const [ready, setReady] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || !onHeightChange) return undefined;
    const observer = new ResizeObserver((entries) => {
      const height = entries[0]?.contentRect?.height;
      if (height) onHeightChange(height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [onHeightChange]);

  return (
    <Box
      ref={wrapperRef}
      sx={{
        position: 'relative', width: '100%', maxWidth: 480, background: '#eee',
        flexShrink: 0, alignSelf: 'flex-start',
      }}
    >
      <Box
        component="img"
        src={imagesrc}
        alt="Scene"
        onLoad={() => setReady(true)}
        sx={{ display: 'block', width: '100%', height: 'auto' }}
      />
      {ready && (
        <Box
          sx={{
            position: 'absolute',
            left: `${box.bboxXPct}%`,
            top: `${box.bboxYPct}%`,
            width: `${box.bboxWidthPct}%`,
            height: `${box.bboxHeightPct}%`,
            border: '3px solid #e11d48',
            boxShadow: '0 0 0 1px rgba(255,255,255,0.85)',
            pointerEvents: 'none',
          }}
        />
      )}
    </Box>
  );
}

// ─── CandidateCard ──────────────────────────────────────────────────────────
// Image-forward card. Clicking anywhere on a PENDING card toggles it
// between "hidden" (default -- neutral border) and "shown to users"
// (green border). There's no separate button, so almost the whole card
// is the material's photo.
//
// Metadata (name/vendor/price/dimensions) only appears in a tooltip on
// hover, via MUI's Tooltip. That's deliberate: MUI's Tooltip renders its
// popup in a layer that sits away from the element it's attached to, so
// hovering to read the metadata never sits on top of (and never eats
// the click for) the card underneath. A plain absolutely-positioned
// overlay div drawn directly over the card would risk doing exactly
// that, so this was chosen specifically to avoid it.
//
// Candidates already decided in an EARLIER review session (qualitycontrol
// already true/false, not null) are shown for side-by-side context but
// aren't clickable here -- there's no pending decision left to make on
// them. They're just faded slightly with a small corner tag instead of a
// coloured border.
//
// This can only happen WITHIN a single region, not across two different
// regions (a material row belongs to exactly one region_id, so there's no
// row-sharing between regions). The realistic case is: a region gets
// fully reviewed, and later on a new candidate material is added to that
// SAME region_id (e.g. a new vendor option). That new row is null, so the
// region reappears in the pending queue, and pulling "every candidate for
// this region" now includes both the new pending one and the older
// already-decided ones alongside it. Until your team has submitted at
// least one review, every candidate you see will be pending/clickable --
// this path is dormant until that first submission happens.
function CandidateCard({ candidate, isPending, approve, onToggle }) {
  const metadata = (
    <Box sx={{ fontSize: '12px', lineHeight: 1.6 }}>
      <Box sx={{ fontWeight: 600 }}>{candidate.namelabel || 'Untitled'}</Box>
      {candidate.vendor && <Box>{candidate.vendor}</Box>}
      {candidate.price && <Box>{formatMaterialPrice(candidate.price)}</Box>}
      {candidate.dimensions && <Box>{candidate.dimensions}</Box>}
    </Box>
  );

  return (
    <Tooltip title={metadata} arrow placement="top" enterDelay={250}>
      <Box
        onClick={isPending ? onToggle : undefined}
        sx={{
          position: 'relative',
          aspectRatio: '1 / 1',
          borderRadius: '6px',
          overflow: 'hidden',
          cursor: isPending ? 'pointer' : 'default',
          opacity: isPending ? 1 : 0.55,
          boxSizing: 'border-box',
          border: '5px solid',
          borderColor: isPending && approve ? '#39FF14' : '#ddd',
          boxShadow: isPending && approve ? '0 0 10px 2px rgba(57, 255, 20, 0.65)' : 'none',
          transition: 'border-color 0.1s ease, box-shadow 0.1s ease',
        }}
      >
        {candidate.imagesrc ? (
          <Box
            component="img"
            src={candidate.imagesrc}
            alt={candidate.namelabel || 'Material'}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <Box sx={{
            width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: '#f0f0f0', fontSize: '11px', color: '#999', textAlign: 'center', padding: '4px',
          }}>
            No image
          </Box>
        )}

        {!isPending && (
          <Box sx={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            fontSize: '10px', textAlign: 'center', padding: '2px 0',
            background: 'rgba(0,0,0,0.55)', color: '#fff', pointerEvents: 'none',
          }}>
            {candidate.qualitycontrol ? 'Already shown' : 'Already hidden'}
          </Box>
        )}
      </Box>
    </Tooltip>
  );
}

// ─── RegionCard ─────────────────────────────────────────────────────────────
// One region: the scene photo on the left, its candidates in a 3-column
// grid on the right.
//
// The grid has no height cap and no scrolling -- it simply grows to fit
// every candidate. Earlier versions tried to cap the grid's height to
// the photo's rendered height (so the two stayed the same height side
// by side), but any such cap -- whether a flat pixel guess or a value
// snapped to whole rows -- inevitably runs short for a region with more
// candidates than a short (landscape) photo has room for, and cropping
// or scrolling a candidate out of view is the wrong trade-off for a QC
// tool: a reviewer needs to see every option, full size, without
// digging for it. So the grid just takes the height it needs. A region
// with a lot of candidates makes this card a bit taller than its
// neighbours -- a normal, expected outcome for a review list.
function RegionCard({ group, decisions, onToggle }) {
  return (
    <Box sx={{
      display: 'flex', gap: '20px', flexWrap: 'wrap',
      border: '1px solid #eee', borderRadius: '6px', padding: '16px',
    }}>
      <BoundingBoxPhoto imagesrc={group.scene.imagesrc} box={group} />

      <Box sx={{ flex: 1, minWidth: 260 }}>
        <Typography sx={{ fontSize: '12px', color: '#888', mb: '8px' }}>
          {group.scene.catRoom} · region #{group.regionId}
        </Typography>

        <Box sx={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px',
        }}>
          {group.candidates.map((candidate) => (
            <CandidateCard
              key={candidate.id}
              candidate={candidate}
              isPending={candidate.qualitycontrol == null}
              approve={!!decisions[candidate.id]}
              onToggle={() => onToggle(candidate.id)}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
}

// ─── MaterialsQc (root) ─────────────────────────────────────────────────────
export default function MaterialsQc() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState(null);
  // { [materialId]: boolean } -- one entry per PENDING candidate currently
  // on screen. Every pending candidate defaults to false (hidden), per
  // spec, whether or not the reviewer ever clicks it.
  const [decisions, setDecisions] = useState({});
  const [error, setError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setGroups(null);
    setError('');
    fetchPendingMaterials()
      .then((data) => {
        setGroups(data);
        const initial = {};
        data.forEach((group) => {
          group.candidates.forEach((c) => {
            if (c.qualitycontrol == null) initial[c.id] = false;
          });
        });
        setDecisions(initial);
      })
      .catch((err) => setError(friendlyMessage(err)));
  };

  useEffect(() => { load(); }, []);

  const handleToggle = (id) => {
    setDecisions((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      const payload = Object.entries(decisions).map(([id, approve]) => ({ id: Number(id), approve }));
      await submitMaterialDecisions(payload);
      load(); // refresh -- reviewed items drop off, next batch (if any) appears
    } catch (err) {
      // The page stays as it is, so no decisions are lost.
      setSubmitError(friendlyMessage(err, "We couldn't save your decisions. Your choices are still on screen; please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return <ErrorState size="page" title="We couldn't load this review" message={error} onRetry={load} />;
  }
  if (!groups) {
    return <LoadingState label="Loading the review…" size="page" />;
  }

  const pendingCount = Object.keys(decisions).length;

  return (
    <Box sx={{ maxWidth: 1100, margin: '0 auto', padding: '20px', fontFamily: 'sans-serif' }}>
      <Box sx={{
        position: 'sticky', top: 0, background: '#fff', zIndex: 1,
        borderBottom: '1px solid #ddd', paddingBottom: '12px', marginBottom: '20px',
      }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', mb: '8px' }}>
          <Box
            component="button"
            onClick={() => navigate('/qc')}
            sx={{
              justifySelf: 'start',
              fontSize: '13px', fontWeight: 600, padding: '10px 18px', borderRadius: '3px',
              cursor: 'pointer', background: '#475569', color: '#fff', border: 'none',
            }}
          >
            ← QC Home
          </Box>
          <Typography sx={{ justifySelf: 'center', fontSize: '20px', fontWeight: 600 }}>
            Material Sourcing QC
          </Typography>
          <Box
            component="button"
            onClick={handleSubmit}
            disabled={submitting || pendingCount === 0}
            sx={{
              justifySelf: 'end',
              fontSize: '13px', fontWeight: 600, padding: '10px 18px', borderRadius: '3px',
              cursor: pendingCount === 0 ? 'default' : 'pointer',
              background: pendingCount === 0 ? '#ccc' : '#16a34a', color: '#fff', border: 'none',
            }}
          >
            {submitting ? 'Submitting…' : `Submit (${pendingCount})`}
          </Box>
        </Box>

        {submitError && <InlineAlert severity="error" sx={{ mb: 1 }}>{submitError}</InlineAlert>}
        <Typography sx={{ fontSize: '12px', color: '#666' }}>
          Click a candidate to mark it as <b>shown to users</b> — its border turns green. Click it again
          to revert to hidden. Hover a candidate to see its name, vendor, and price. Anything you don't
          click stays hidden by default.
        </Typography>
      </Box>

      {groups.length === 0 ? (
        <Typography sx={{ color: '#666' }}>Nothing pending review right now.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
          {groups.map((group) => (
            <RegionCard
              key={group.regionId}
              group={group}
              decisions={decisions}
              onToggle={handleToggle}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
